import { useEffect, useRef, useState } from "react"

export default function useStateWithHistory(initialState) {

  // TODO: Cap the history stack to a certain number of entries


  const throttleTimespan = 500 // ms

  const internalChange  = useRef(false)
  const historyStack    = useRef([])
  const historyPosition = useRef(-1)
  const lastChange      = useRef({})

  const [state,      setState    ] = useState(initialState)
  const [squashKey,  setSquashKey] = useState('')
  const [history,    setHistory  ] = useState(historyData())


  function historyData() {
    return {
      canUndo: historyPosition.current > 0,
      canRedo: historyPosition.current < historyStack.current.length - 1,
      undo:    () => jumpTo(historyPosition.current - 1),
      redo:    () => jumpTo(historyPosition.current + 1),
      length:  historyStack.current.length - 1,
      index:   historyPosition.current,
      squashKey,
      setSquashKey,
    }
  }


  useEffect(() => {
    // If this change was triggered by undo/redo, don't record it in the history.
    if (internalChange.current) {
      lastChange.current = {}
      internalChange.current = false
      return
    }

    // Make any tweaks to the history stack before adding the new state.
    if (historyPosition.current < historyStack.current.length - 1) {
      // If we've are in the middle of the history stack, remove all future
      // states before adding the new state.
      historyStack.current = historyStack.current.slice(0, historyPosition.current + 1)
    } else if (shouldSquash()) {
      // If we are squashing, remove the last state in the stack
      // before adding the new state.
      historyStack.current.pop()
    }
    historyPosition.current = historyStack.current.length - 1

    const oldState = historyStack.current[historyPosition.current]
    const oldStateValue = JSON.stringify(oldState)
    const newStateValue = JSON.stringify(state)

    if (newStateValue !== oldStateValue) {

      historyStack.current.push(state)
      historyPosition.current = historyStack.current.length - 1

      const now = new Date().valueOf()
      lastChange.current = {time: now, key: squashKey || ''}
    }

    setHistory(historyData())
  }, [state])


  function shouldSquash() {
    if (historyStack.current.length === 0) return false

    // Squash consecutive changes using the same history key
    if (squashKey && squashKey === lastChange.current?.key) {
      return true
    }

    // Ignore multiple changes in a short time window
    if (lastChange.current?.time) {
      const now = new Date().valueOf()
      return now - lastChange.current.time < throttleTimespan
    }
  }


  function jumpTo(position) {
    // Make sure the position is within the bounds of the history stack
    position = Math.max(0, position)
    position = Math.min(position, historyStack.current.length - 1)

    // If the position has changed, update the state
    if (position !== historyPosition.current) {
      internalChange.current = true
      historyPosition.current = position
      setState(historyStack.current[position])
      setHistory(historyData())
      setSquashKey('')
    }
  }



  //----------------------------------------------------------------------------
  // NOTE: If the client wants to squash several updates into one, it should
  // call setSquashKey(key) before calling setState(newState). Any changes made
  // after that will be squashed into the same history entry until the key is
  // changed again. If the key is not specified, the squashing will only happen
  // if the changes are made within the throttle time window.
  //----------------------------------------------------------------------------

  return {
    state,
    setState,
    history,
  }

}
